import { Controller } from "@hotwired/stimulus";
import { updateFilterCount } from "../javascript/leads_filter_chips";

// Main drawer controller. Composes with filter-diff via outlet.
//
// Responsibilities:
//   - Apply orchestration (pushState + DT ajax + aria-live announce +
//     Applied-checkmark + localStorage write). NOTE: chips_html, result_count,
//     and the chip_count badge are owned by the DataTables `dataSrc` callback
//     (_table.html.erb), which applies them on EVERY DT ajax — Apply included.
//     The drawer only announces (see _afterApply / BUG #8).
//   - Reset (revert draft to applied baseline)
//   - removeFilter (chip ×) and clearAll (chip Clear all) via Stimulus
//   - "URL changed underneath" notice when drawer is open and URL mutates
//   - "Restore previous edits?" link in header on next open when last
//     close had a non-empty draft
//   - Custom focus-trap (Bootstrap 5.1.3 offcanvas does not trap focus;
//     5.2+ does. A2 upgrade was skipped, so we trap manually.)
//
// Markup contract:
//   <div data-controller="filter-drawer filter-diff filter-presets"
//        data-filter-drawer-leads-url-value="/exhibitor/leads"
//        data-filter-drawer-storage-key-value="leads-filter-last:<u>:<x>:<ee>"
//        data-filter-drawer-filter-diff-outlet="#leads-filter-drawer"
//        id="leads-filter-drawer"
//        class="offcanvas offcanvas-end filter-drawer">
//     ...
//     <button data-action="filter-drawer#apply" data-filter-diff-target="applyBtn">Apply</button>
//     <button data-action="filter-drawer#reset">Reset all</button>
//     <div data-filter-drawer-target="urlChangeNotice" hidden>...</div>
//     <div data-filter-drawer-target="restoreEditsNotice" hidden>...</div>
//     <div data-filter-drawer-target="announcer" class="visually-hidden" aria-live="polite"></div>
//   </div>
export default class extends Controller {
  static targets = ["announcer", "urlChangeNotice", "restoreEditsNotice", "summary"];
  static outlets = ["filter-diff"];
  static values = {
    leadsUrl:   String,
    storageKey: String,
    // LeadFilters::ARRAY_PARAM_KEYS, fed in from the server (the single source
    // of truth) so reloadDraft knows which bare URL keys are array-shaped even
    // when the URL used the SCALAR form (?country=Canada, not ?country[]=…).
    // See reloadDraft. Defaults to [] so the controller stays safe if the
    // attribute is absent (older cached markup / non-drawer render paths).
    arrayKeys:  { type: Array, default: [] },
  };

  connect() {
    this._triggerEl = document.querySelector("[data-filter-drawer-trigger]");
    this._appliedTimer = null;
    this._popHandler = this._onPopState.bind(this);
    window.addEventListener("popstate", this._popHandler);

    // Bootstrap offcanvas lifecycle events
    this._shownHandler = this._onShown.bind(this);
    this._hiddenHandler = this._onHidden.bind(this);
    this.element.addEventListener("shown.bs.offcanvas", this._shownHandler);
    this.element.addEventListener("hidden.bs.offcanvas", this._hiddenHandler);

    // Rep-filter coherence: filter_diff.applyValues deliberately SKIPS the rep
    // group (the `_include_via_territory` UI-only control + the dual-named rep
    // <select>), so we re-sync the whole group here — colocated with
    // repFilterToggle — from the values-applied event's full hash.
    this._valuesAppliedHandler = this._onValuesApplied.bind(this);
    this.element.addEventListener("filter-diff:values-applied", this._valuesAppliedHandler);

    // Custom focus-trap handler (bound once so addEventListener /
    // removeEventListener reference the same function).
    this._focusTrapHandler = this._focusTrap.bind(this);

    // NOTE: The drawer no longer auto-opens on drilldown arrival. A drilldown
    // lands with filters APPLIED but the drawer CLOSED — the server-rendered
    // back chip and the "Showing N leads matching these filters" count already
    // communicate the active filtering. The drawer opens ONLY when the user
    // clicks the Filters trigger button.

    // Show "Restore previous edits?" link if applicable
    if (this.hasRestoreEditsNoticeTarget) {
      const cached = this._readUnappliedDraft();
      if (cached) this.restoreEditsNoticeTarget.hidden = false;
    }
  }

  disconnect() {
    window.removeEventListener("popstate", this._popHandler);
    this.element.removeEventListener("shown.bs.offcanvas", this._shownHandler);
    this.element.removeEventListener("hidden.bs.offcanvas", this._hiddenHandler);
    this.element.removeEventListener("filter-diff:values-applied", this._valuesAppliedHandler);
    // Defensive: if the drawer is disconnected while open, remove the trap.
    this.element.removeEventListener("keydown", this._focusTrapHandler);
    clearTimeout(this._appliedTimer);
  }

  // ─── Apply ────────────────────────────────────────────────────────────────
  async apply(event) {
    event?.preventDefault?.();
    if (!this.hasFilterDiffOutlet) return;
    const draft = this.filterDiffOutlet.serializeDraft();
    const newPath = this._buildUrl(this.leadsUrlValue, draft);
    const jsonPath = this._buildUrl(this.leadsUrlValue + ".json", draft);

    window.history.pushState(null, "", newPath);

    if (window.$ && window.$.fn.DataTable && window.$.fn.DataTable.isDataTable("#table-leads")) {
      const dt = window.$("#table-leads").DataTable();
      dt.ajax.url(jsonPath).load((json) => this._afterApply(json));
    } else {
      this._afterApply(null);
    }

    // Optimistic trigger-badge update
    this._updateTriggerBadge(draft);

    // Applied-checkmark transient state.
    // The rebaseline that used to ride this timer moved to the end of apply()
    // (see below). Deferring it was also a latent footgun: the drawer now HIDES
    // on Apply, so a user who reopened it inside the 1500ms window and edited an
    // input had that edit silently absorbed into the baseline — marking a genuinely
    // unapplied edit "clean". The timer now only retires the visual state.
    if (this.filterDiffOutlet.hasApplyBtnTarget) {
      clearTimeout(this._appliedTimer);
      const btn = this.filterDiffOutlet.applyBtnTarget;
      btn.classList.add("is-applied");
      btn.textContent = "Applied ✓";
      this._appliedTimer = setTimeout(() => {
        btn.classList.remove("is-applied");
      }, 1500);
    }

    // Persist last-applied to localStorage
    this._writeLastApplied(draft);

    // LRPO-5507 (item 17) — get out of the user's way. Apply exists to show them
    // the filtered results; leaving the panel open parks it on top of them. The
    // aria-live announcement in _afterApply carries the confirmation the (now
    // hidden) "Applied ✓" button flash used to.
    //
    // REBASELINE BEFORE HIDING. _hidePanel() fires hidden.bs.offcanvas ->
    // _onHidden() -> isDirty(), and the ONLY rebaseline used to sit on the 1500ms
    // "Applied ✓" timer below — so _onHidden ran while the baseline was still the
    // PRE-apply one, read the just-applied draft as "unapplied edits", and stashed
    // it. Every successful Apply therefore armed the spurious "restore unsaved
    // edits?" banner that BUG #10 had already fixed for the open/close path.
    // Rebaselining here makes isDirty() false, so _onHidden clears the stash
    // instead of writing one; the removeItem is belt-and-braces (and mirrors
    // reset()) for the case where the offcanvas event never fires at all.
    this.filterDiffOutlet.rebaseline();
    sessionStorage.removeItem(this._unappliedKey());
    this._hidePanel();
  }

  _afterApply(json) {
    // BUG #8: chips_html, result_count, and chip_count are applied by the
    // DataTables dataSrc callback (_table.html.erb) on every DT ajax — incl.
    // this Apply — so doing them here was redundant AND buggy: dataSrc
    // replaceWith-DETACHES the chips node, then this._chipRowEl.outerHTML threw
    // "NoModificationAllowedError" (element has no parent node). Because that
    // was _afterApply's FIRST statement, the throw also aborted this announce.
    // dataSrc owns the DOM; the drawer only fires the aria-live announcement.
    if (json && json.result_count != null && this.hasAnnouncerTarget) {
      this.announcerTarget.textContent =
        `Filters applied. Showing ${json.result_count} leads.`;
    }
  }

  // ─── Reset all (clear the filters and re-run the query) ───────────────────
  // LRPO-5507 (item 16). This used to call applyValues(appliedValue) — i.e. it
  // reverted the DRAFT to the currently-APPLIED baseline. That is "discard my
  // unsaved edits", not "reset": it cleared nothing, navigated nowhere, and on a
  // pristine drawer with filters applied it was a literal no-op, which is why QA
  // saw the data stay filtered until they also pressed Apply. Reset now means
  // CLEAR: blank the draft, then navigate off the same clean base clearAll uses
  // (preserving the drilldown context, so a reset inside a report drill keeps its
  // origin chip and comparison event).
  reset(event) {
    event?.preventDefault?.();
    if (this.hasFilterDiffOutlet) {
      this.filterDiffOutlet.applyValues({});
      // Same hazard as apply(): _hidePanel() -> hidden.bs.offcanvas -> _onHidden()
      // -> isDirty() against the PRE-reset baseline, which would re-stash the
      // now-blank draft as "unapplied edits" right after we cleared it. (The
      // navigation below usually outruns the offcanvas transition, so this was a
      // RACE rather than a certainty — rebaselining makes it deterministic.)
      this.filterDiffOutlet.rebaseline();
    }
    // The drawer is navigating away — a stashed draft from a previous close would
    // otherwise resurface as a bogus "restore unsaved edits?" prompt.
    sessionStorage.removeItem(this._unappliedKey());
    this._hidePanel();
    this._navigateWith(this._preservedContext());
  }

  // ─── Chip × click ─────────────────────────────────────────────────────────
  // Resolved via event.target.closest so the handler works regardless of
  // listener scope — the chip row is a sibling of the drawer in the page DOM,
  // so the click is forwarded here via the filter-chip-row controller's
  // outlet delegation (event never bubbles through the drawer's offcanvas).
  removeFilter(event) {
    event.preventDefault();
    const btn = event.target.closest("[data-remove-params]");
    if (!btn) return;
    const removeParams = JSON.parse(btn.dataset.removeParams || "{}");
    this._navigateWith(removeParams);
  }

  // ─── Clear all chips ──────────────────────────────────────────────────────
  clearAll(event) {
    event.preventDefault();
    this._navigateWith(this._preservedContext());
  }

  // The drilldown context a "clear the filters" navigation must carry through:
  // where the user drilled in FROM (so the back chip survives) and which event
  // they are comparing against. Shared by clearAll and reset.
  _preservedContext() {
    const sp = new URLSearchParams(window.location.search);
    const preserve = {};
    ["segment_origin", "segment_anchor", "target_event_id"].forEach((k) => {
      const v = sp.get(k);
      if (v) preserve[k] = v;
    });
    return preserve;
  }

  // Bootstrap's offcanvas API. Absent in jsdom and on any page that renders the
  // drawer markup without the pack — a soft no-op there, never a throw.
  _hidePanel() {
    const Offcanvas = window.bootstrap && window.bootstrap.Offcanvas;
    if (!Offcanvas) return;
    Offcanvas.getOrCreateInstance(this.element).hide();
  }

  // ─── Remove the "Compared with <event>" chip (LRPO-5153) ──────────────────
  // Inverse of clearAll: keep every applied filter from the current URL but
  // drop target_event_id, then navigate off the clean leads base. Mirrors the
  // old server <a href> intent (request.query_parameters.except(
  // "target_event_id")) WITHOUT the DataTables-param pollution that a JSON
  // chips_html render baked in — and without ever pointing at the JSON
  // endpoint, so the close can no longer dump raw JSON over the page.
  // DataTables pagination params live in the AJAX URL, not normally the address
  // bar — but if one ever leaks into window.location.search it must NOT ride
  // along into the destination (that noise is what pointed the old comparison
  // close <a href> back at the JSON endpoint). Dropped defensively here.
  static DATATABLES_PARAM_KEYS = ["draw", "start", "length", "page_number"];

  removeCompare(event) {
    event.preventDefault();
    // Preserve the applied filter state from the URL, minus target_event_id
    // (and DataTables noise). Array-shaped keys arrive as "key[]"; _buildUrl
    // re-appends the "[]" so we strip it here to hand bare keys + multi-value
    // arrays to the builder.
    const drop = new Set(["target_event_id", ...this.constructor.DATATABLES_PARAM_KEYS]);
    const sp = new URLSearchParams(window.location.search);
    const params = {};
    for (const rawKey of new Set([...sp.keys()])) {
      const bareKey = rawKey.endsWith("[]") ? rawKey.slice(0, -2) : rawKey;
      if (drop.has(bareKey)) continue;
      const values = sp.getAll(rawKey);
      params[bareKey] = rawKey.endsWith("[]") ? values : values[0];
    }
    this._navigateWith(params);
  }

  _navigateWith(params) {
    // Hard navigation — same path as the existing chip × <a href>, but
    // built from a Stimulus action so the value comes from data-remove-params.
    const url = this._buildUrl(this.leadsUrlValue, params);
    window.location.href = url;
  }

  // ─── URL-change-while-open notice ─────────────────────────────────────────
  _onPopState() {
    if (!this.element.classList.contains("show")) return; // drawer not open
    if (!this.hasUrlChangeNoticeTarget) return;
    this.urlChangeNoticeTarget.hidden = false;
  }

  reloadDraft() {
    // Action handler for the "Reload" button inside the URL-change notice.
    // v3 C3: Use getAll() per-key so country[]=A&country[]=B yields ["A","B"]
    // (NOT { "country[]": "B" } as Object.fromEntries(URLSearchParams) would).
    // Strip trailing "[]" so the bare key drives filterDiff.applyValues.
    //
    // SCALAR-drill fix: the server (LeadFilters.coerce_scalar_array_params)
    // makes SCALAR drilldown URLs (?country=Canada, ?quality_band=buyer) into
    // live filters by coercing each ARRAY_PARAM_KEYS scalar into a 1-element
    // Array. But that URL key has NO "[]" suffix, so the old `endsWith("[]") ?
    // values : values[0]` handed applyValues a STRING ("Canada"). The desync
    // hit TRUE <select multiple> controls only (country/state/city + the other
    // multi-selects): their applyValues branch does NOT scalar-wrap
    // (`Array.isArray(value) ? value : []`), so a string CLEARED the select —
    // silently dropping the filter. Pill-group / chip-input rebuild from the
    // values-applied event but already scalar-wrap their input
    // (`Array.isArray(raw) ? raw : raw == null ? [] : [raw]`), so they did NOT
    // clear under the old scalar reloadDraft; normalizing here is harmless for
    // them and keeps ONE consistent array shape across all consumers.
    //
    // Normalize HERE (the single chokepoint): an array-shaped key ALWAYS
    // becomes an Array, regardless of the scalar-vs-"[]" URL shape, driven by
    // ARRAY_PARAM_KEYS (the server constant, fed in via the drawer DOM, so no
    // client-side copy drifts). getAll() preserves a comma-bearing value as ONE
    // element ("Bonaire, Sint Eustatius and Saba" never splits). Non-array keys
    // (q, company_name, date_preset, …) stay scalar exactly as before.
    const sp = new URLSearchParams(window.location.search);
    const out = {};
    for (const rawKey of new Set([...sp.keys()])) {
      const bareKey = rawKey.endsWith("[]") ? rawKey.slice(0, -2) : rawKey;
      const values = sp.getAll(rawKey);
      const isArrayKey = rawKey.endsWith("[]") || this.arrayKeysValue.includes(bareKey);
      out[bareKey] = isArrayKey ? values : values[0];
    }
    this.filterDiffOutlet.applyValues(out);
    this.filterDiffOutlet.rebaseline();
    if (this.hasUrlChangeNoticeTarget) this.urlChangeNoticeTarget.hidden = true;
  }

  keepDraft() {
    if (this.hasUrlChangeNoticeTarget) this.urlChangeNoticeTarget.hidden = true;
  }

  // ─── Restore previous edits (on close with non-empty draft, on reopen) ────
  _onShown() {
    // LRPO-5926 — the tawk.to launcher floats bottom-right with a huge
    // z-index, straight over this drawer's sticky Apply/Reset footer. Hide
    // it while the drawer is open; _onHidden restores it. Guarded: the API
    // only exists once the embed script has loaded.
    window.Tawk_API?.hideWidget?.();
    this._triggerEl?.setAttribute("aria-pressed", "true");
    const cached = this._readUnappliedDraft();
    if (cached && this.hasRestoreEditsNoticeTarget) {
      this.restoreEditsNoticeTarget.hidden = false;
    }
    // Activate custom focus-trap (Bootstrap 5.1.3 does not provide one).
    this.element.addEventListener("keydown", this._focusTrapHandler);
    this._focusFirstElement();
  }

  _onHidden() {
    // LRPO-5926 — restore the chat launcher hidden by _onShown.
    window.Tawk_API?.showWidget?.();
    this._triggerEl?.setAttribute("aria-pressed", "false");
    // Deactivate focus-trap.
    this.element.removeEventListener("keydown", this._focusTrapHandler);
    // If draft has real (unapplied) edits, stash it for the next open.
    // BUG #10: the old JSON.stringify(serializeDraft()) !== JSON.stringify(
    // appliedValue) comparison was key-order-sensitive AND mismatched the
    // verbose draft against the compact-blank applied baseline, so it fired on
    // EVERY close of a pristine drawer → a spurious "restore unsaved edits?"
    // notice on every open. Delegate the dirty decision to filter_diff.isDirty()
    // (order-insensitive, blank-tolerant, against the SAME baseline the Apply
    // button uses). Still stash serializeDraft() verbatim when dirty.
    if (this.hasFilterDiffOutlet) {
      if (this.filterDiffOutlet.isDirty()) {
        const draft = this.filterDiffOutlet.serializeDraft();
        sessionStorage.setItem(this._unappliedKey(), JSON.stringify(draft));
      } else {
        sessionStorage.removeItem(this._unappliedKey());
      }
    }
  }

  restorePreviousEdits() {
    const cached = this._readUnappliedDraft();
    if (!cached) return;
    this.filterDiffOutlet.applyValues(cached);
    sessionStorage.removeItem(this._unappliedKey());
    if (this.hasRestoreEditsNoticeTarget) this.restoreEditsNoticeTarget.hidden = true;
  }

  dismissRestoreEdits() {
    sessionStorage.removeItem(this._unappliedKey());
    if (this.hasRestoreEditsNoticeTarget) this.restoreEditsNoticeTarget.hidden = true;
  }

  repFilterToggle(event) {
    const checkbox = event.target;
    const field = checkbox.closest(".filter-field");
    const select = field?.querySelector("select");
    if (!select) return;
    // v3 I1: After array-params migration, rep selects post []-suffixed keys.
    select.name = checkbox.checked ? "rep_id[]" : "assigned_rep_id[]";
    select.dispatchEvent(new Event("input", { bubbles: true }));
  }

  // ─── Rep-filter coherence on Reset / preset-load / reloadDraft / restore ──
  // filter_diff.applyValues skips the rep group; we re-derive its mode from the
  // applied hash so the checkbox, the dual-named <select>'s name, AND its
  // selectedness stay coherent. Mode rule (mirrors _assignment.html.erb):
  //   • non-empty assigned_rep_id → DIRECT  (unchecked, name "assigned_rep_id[]")
  //   • otherwise (rep_id, or neither) → TERRITORY/default (checked, "rep_id[]")
  _onValuesApplied(event) {
    this._syncRepFilterFromValues(event?.detail?.values || {});
  }

  _syncRepFilterFromValues(values) {
    const checkbox = this.element.querySelector('input[name="_include_via_territory"]');
    if (!checkbox) return; // rep section not rendered for this exhibitor
    const field = checkbox.closest(".filter-field");
    const select = field?.querySelector("select");
    if (!select) return;

    const direct = this._nonEmpty(values.assigned_rep_id);
    const activeKey = direct ? "assigned_rep_id" : "rep_id";

    // Mode first (checkbox + select name), THEN selectedness for the ACTIVE key
    // (the select is dual-named, so its value must be set against the new name).
    checkbox.checked = !direct;
    select.name = `${activeKey}[]`;

    const wanted = this._asArray(values[activeKey]);
    if (select.multiple) {
      Array.from(select.options).forEach((opt) => {
        opt.selected = wanted.includes(opt.value);
      });
    } else {
      select.value = wanted[0] != null ? wanted[0] : "";
    }
    // Repaint a Select2-backed rep select (namespaced refresh; see filter_diff).
    if (window.$) {
      const $sel = window.$(select);
      if ($sel.data && $sel.data("select2")) $sel.trigger("change.select2");
    }
  }

  _nonEmpty(v) {
    if (v == null) return false;
    if (Array.isArray(v)) return v.some((x) => x != null && String(x) !== "");
    return String(v) !== "";
  }

  _asArray(v) {
    if (v == null) return [];
    return (Array.isArray(v) ? v : [v]).map(String).filter((x) => x !== "");
  }

  _readUnappliedDraft() {
    try {
      const raw = sessionStorage.getItem(this._unappliedKey());
      if (!raw) return null;
      return JSON.parse(raw);
    } catch { return null; }
  }

  _unappliedKey() { return `${this.storageKeyValue}:unapplied-draft`; }

  // ─── localStorage last-applied write ──────────────────────────────────────
  _writeLastApplied(draft) {
    if (!this.storageKeyValue) return;
    if (Object.keys(draft).length === 0) {
      localStorage.removeItem(this.storageKeyValue);
      return;
    }
    try {
      localStorage.setItem(this.storageKeyValue, JSON.stringify({
        params: draft, savedAt: new Date().toISOString()
      }));
    } catch { /* quota exhausted — soft fail */ }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────
  _updateTriggerBadge(draft) {
    // preset_id (LRPO-5507) is drawer state — which preset is selected — not a
    // filter. The server never counts it (it isn't in PERMITTED_PARAM_SHAPE and
    // produces no chip), so the optimistic count must not either.
    const exclude = new Set(["target_event_id", "segment_origin", "segment_anchor", "q", "preset_id", "_include_via_territory", "include_via_territory"]);
    // v3 I2: An empty Array ([]) is truthy under the old `v != null && v !== ""`
    // predicate, which inflates the badge by 1 per empty-array key. The isSet
    // helper handles arrays explicitly via .length.
    const isSet = (v) => v != null && (Array.isArray(v) ? v.length > 0 : v !== "");
    // Buyer-profile group: collapses 5 keys into 1 chip
    const buyerProfileKeys = ["business_type", "company_type", "position", "volume", "price_point"];
    const buyerActive = buyerProfileKeys.some((k) => isSet(draft[k]));
    const otherKeys = Object.keys(draft).filter((k) =>
      !exclude.has(k) && !k.startsWith("_") && !buyerProfileKeys.includes(k) && isSet(draft[k])
    );
    const count = otherKeys.length + (buyerActive ? 1 : 0);
    this._updateBadgeNumber(count);
  }

  // LRPO-5507 — ONE writer for BOTH count surfaces (the trigger badge and the
  // drawer header's "N filters applied"), shared with the DataTables dataSrc
  // callback, so the optimistic write and the authoritative server write can
  // never leave the two disagreeing.
  _updateBadgeNumber(count) {
    updateFilterCount(count);
  }

  _buildUrl(base, params) {
    const url = new URL(base, window.location.origin);
    // Clear all existing filter params before applying new ones.
    // Snapshot keys via Array.from so deletes mid-iteration are safe.
    Array.from(url.searchParams.keys()).forEach((k) => url.searchParams.delete(k));
    Object.entries(params).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        v.forEach((item) => url.searchParams.append(`${k}[]`, item));
      } else if (v != null && v !== "") {
        url.searchParams.set(k, v);
      }
    });
    return url.pathname + (url.searchParams.toString() ? "?" + url.searchParams.toString() : "");
  }

  // ─── Custom focus-trap (Phase A2 was skipped; Bootstrap 5.1.3 lacks one) ──
  _focusTrap(event) {
    if (event.key !== "Tab") return;
    const focusable = this.element.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  }

  _focusFirstElement() {
    const focusable = this.element.querySelector(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable) focusable.focus();
  }
}
